import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { PAGE_SIZE } from "@shared/utils/constants";
import { downloadFile } from "@shared/utils/utils";
import {
  confirmUploadAssets,
  createAsset,
  deleteAsset,
  downloadAssets,
  getAsset,
  getAssetClassList,
  getAssetDetail,
  getAssetList,
  getAssetStatusList,
  getAssetSummary,
  getAssetTypeList,
  getConditionAssessmentDetail,
  getDepreciationMethodList,
  getDimensionList,
  getEnegyAssetDetail,
  getEnergyAssets,
  getManufacturer,
  getOAndM,
  getPerformanceDetail,
  getSystemNetworkList,
  updateAsset,
  updateEnergyAsset,
  uploadAssetFile,
} from "./api";

// Asset Hooks
export const useCreateAsset = () => {
  return useSmartMutation((payload: any) => createAsset(payload), {
    onSuccess: () => {
      toast({
        title: "Asset created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.assets.assetList().slice(0, 4);
      const dimensionKey = QueryKeyFactory.module.ax.assets
        .assetDimensionFieldsList()
        .slice(0, 4);
      queryClient.refetchQueries({
        queryKey: key,
      });
      queryClient.refetchQueries({
        queryKey: dimensionKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create asset: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAsset = (id: string, type?: string) => {
  return useSmartMutation((payload: any) => updateAsset(id, payload), {
    onSuccess: (_data) => {
      if (type === "documents") {
        toast({
          title: "File Uploaded successfully!",
          variant: "default",
        });
      } else {
        toast({
          title: "Asset updated successfully!",
          variant: "default",
        });
      }
      const assetDetailKey = QueryKeyFactory.module.ax.assets.assetDetail(id);
      queryClient.refetchQueries({
        queryKey: assetDetailKey,
      });
      const key = QueryKeyFactory.module.ax.assets.assetList().slice(0, 4);
      const dimensionKey = QueryKeyFactory.module.ax.assets
        .assetDimensionFieldsList()
        .slice(0, 4);

      queryClient.refetchQueries({
        queryKey: dimensionKey,
      });

      queryClient.invalidateQueries({
        queryKey: key,
      });
      queryClient.removeQueries({
        queryKey: QueryKeyFactory.module.ax.assets.assetList({
          limit: PAGE_SIZE,
          page: 1,
        }),
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useSystemNetworkList = (facilityId?: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.systemNetworkList(facilityId),
    () => getSystemNetworkList(facilityId)
  );
};

export const useAssetStatusList = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.assets.assetStatusList(), () =>
    getAssetStatusList()
  );
};

export const useDepreciationMethodList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.depreciationMethodList(),
    () => getDepreciationMethodList()
  );
};

// Asset Class Hooks
export const useAssetClassList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetClassList(),
    () => getAssetClassList()
  );
};

export const useAssetTypeList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetTypeList(code),
    () => getAssetTypeList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useGetManuFacturer = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.manufactureOptions(),
    () => getManufacturer()
  );
};

export const useGetAssets = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.assets.assetOptions(), () =>
    getAsset()
  );
};

export const useDimensionList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.assetDimensionFieldsList(code),
    () => getDimensionList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useAssetList = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.assetList(queryParams),
    () => getAssetList(queryParams)
  );
};

export const useAssetDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.assetDetail(id),
    () => getAssetDetail(id),
    {
      enabled: Boolean(id),
    }
  );
};

export const useConditionAssessmentDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.conditionAssessmentDetail(id),
    () => getConditionAssessmentDetail(id),
    {
      enabled: Boolean(id),
    }
  );
};

export const usePerformanceDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.performanceDetail(id),
    () => getPerformanceDetail(id),
    {
      enabled: Boolean(id),
    }
  );
};

export const useAssetSummary = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.assets.assetSummary(), () =>
    getAssetSummary()
  );
};

export const useOAndM = (id: string, search: string) => {
  return useSmartQuery(QueryKeyFactory.module.ax.assets.oAndM(id, search), () =>
    getOAndM(id, search)
  );
};

export const useDeleteAsset = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({ assetId }: { assetId: string }): Promise<any> => {
      return deleteAsset(assetId, remoteUtilityId);
    },
    {
      onSuccess: (data, { assetId }) => {
        toast({
          title: "Success!",
          description: "Asset deleted successfully",
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.assets.assetList().slice(0, 4),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.assets.assetSummary().slice(0, 4),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete asset",
          variant: "destructive",
        });
      },
    }
  );
};

//energy

export const useDownloadAssets = (type: string) => {
  return useSmartMutation(async () => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0];
    const filename = `asset-${type}-${formattedDate}.csv`;
    const blob = await downloadAssets(type);
    downloadFile(blob, filename);
    return blob;
  });
};

export const useEnergyAssets = (type: string, currentPage: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.energyAssets(type, currentPage),
    () => getEnergyAssets(type, currentPage)
  );
};

export const useUploadAssetFile = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartMutation((formData: FormData): Promise<any> => {
    formData.append("remote_utility_id", utilityId);
    return uploadAssetFile(formData);
  });
};

export const useConfirmUploadAsset = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartMutation((payload) => confirmUploadAssets(payload), {
    onSuccess: (data, variables) => {
      toast({
        title: "Success!",
        description: "Asset upload confirmed successfully",
      });

      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.ax.assets.energyAssets().slice(0, 4),
      });

      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.ax.assets.assetSummary().slice(0, 4),
      });
    },
  });
};

export const useUpdateEnergyAsset = (id: string, type?: string) => {
  return useSmartMutation((payload: any) => updateEnergyAsset(id, payload), {
    onSuccess: (_data) => {
      const key = QueryKeyFactory.module.ax.assets
        .energyAssets(type)
        .slice(0, 4);
      queryClient.refetchQueries({
        queryKey: key,
      });
      toast({
        title: `Asset Updated Successfully`,
      });
    },
  });
};

export const useEnergyAssetDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.assets.assetEnergyDetail(id),
    () => getEnegyAssetDetail(id)
    // {
    //   enabled: Boolean(id),
    // }
  );
};
